import type { Block } from '@w/content-blocks'
import { s } from '@w/schema'

export const LANGUAGES = ['en', 'de', 'ru'] as const
export type Language = (typeof LANGUAGES)[number]

export type Pos =
  | 'NOUN'
  | 'VERB'
  | 'ADJ'
  | 'ADV'
  | 'PRON'
  | 'DET'
  | 'NUM'
  | 'ADP'
  | 'CCONJ'
  | 'SCONJ'
  | 'PART'
  | 'INTJ'
export type Cefr = 'A1' | 'A2' | 'B1' | 'B2' | 'C1' | 'C2'
export type TranslationLanguage = string
export type Span = [offset: number, length: number]

export type Entry = {
  id: string
  language: Language
  headword: string
  transcription: string
  audioSrc?: string
  translations: Record<TranslationLanguage, string>
  /**
   * Keyed by sense id, never a positional array. An adopted entry is CRDT
   * merged and shared inside a workspace: two people editing different senses
   * of the same word must both survive, which a positional array cannot do.
   * Iterate with `entrySenses` — never `Object.values`.
   */
  senses: Record<string, Sense>
}

export type Lexeme = Entry & {
  kind: 'lexeme'
  pos: Pos
  inflectionClass: string
  /**
   * Keyed by cell. The cell IS the form's identity — that is why a WordForm has
   * no id. Iterate with `entryParadigm` for canonical cell order.
   */
  paradigm: Record<string, WordForm>
  readAloudQuestionId: string
  dictationQuestionId: string
}

export type MultiwordExpression = Entry & { kind: 'mwe' }

export type Sense = {
  id: string
  gloss: string
  translations: Record<TranslationLanguage, string>
  cefr: Cefr
  labels: string[]
  example: Example
  senseRecallQuestionId?: string
  formRecallQuestionId?: string
}

export type WordForm = {
  cell: string
  form: string
  transcription: string
  example: Example
  formRecallQuestionId?: string
}

export type Example = {
  text: string
  node: Span
  translations: Record<TranslationLanguage, string>
  nodeAudioSrc?: string
  imagePrompt?: string
  imageSrc?: string
}

export type LexiconEntry = Lexeme | MultiwordExpression

type CachedEntryBase = Omit<Entry, 'senses'> & {
  senses: Record<string, CachedSense>
}

export type CachedLexeme = CachedEntryBase & {
  kind: 'lexeme'
  pos: Pos
  inflectionClass: string
  paradigm: Record<string, CachedWordForm>
}

export type CachedMultiwordExpression = CachedEntryBase & { kind: 'mwe' }
export type CachedEntry = CachedLexeme | CachedMultiwordExpression

export type CachedSense = Sense

export type CachedWordForm = WordForm

// D1: `members` is deleted from stored content. It is an unbounded array that
// grew with every adoption. Class membership is derived from durable adopted
// entry pages where inflectionClass = rule.key, never stored on the block.
// A rule owns one class card; `qid` is that card's business identity, persisted
// with the block when the rule page is written.
export type RuleBlock = Omit<Extract<Block, { type: 'rule' }>, 'members'> & {
  language: Language
  qid?: string
}

/** A rule owns one class card; its business identity is the persisted qid. */
export function ruleQid(rule: RuleBlock): string {
  const qid = rule.qid
  if (!qid) throw new Error('Rule missing question id')
  return qid
}

export type CachedRule = {
  // Older device and server cache rows predate generated human titles.
  title?: string
  language: Language
  key: string
  block: RuleBlock
  updatedAt?: Date
}

export const ruleBlockSchema = s.custom<RuleBlock>(
  (value) =>
    typeof value === 'object' &&
    value !== null &&
    'type' in value &&
    value.type === 'rule' && validRuleAffix(value),
  'Invalid rule block',
)

function validRuleAffix(value: object): boolean {
  if (!('affix' in value) || value.affix === undefined) return true
  const affix = value.affix
  return 'scope' in value && value.scope === 'derivation' &&
    'predicts' in value && Array.isArray(value.predicts) && value.predicts.length === 0 &&
    typeof affix === 'object' && affix !== null &&
    'kind' in affix && (affix.kind === 'prefix' || affix.kind === 'suffix') &&
    'form' in affix && typeof affix.form === 'string' && affix.form.trim().length > 0 &&
    'meaning' in affix && typeof affix.meaning === 'string' && affix.meaning.trim().length > 0
}

export const cachedRuleSchema = s.object({
  language: s.enum(LANGUAGES), key: s.string(), title: s.string().optional(),
  block: ruleBlockSchema,
})


export type LexiconHit = {
  entryId: string
  kind: LexiconEntry['kind']
  headword: string
  pos?: Pos // absent for mwe rows
  transcription: string
  matchedField?: 'headword' | 'forms' | 'translations'
  matchedTranslation?: string
  matchedForm?: string
  translations: Record<TranslationLanguage, string>
  origin: 'pages' | 'cache'
  pageId?: string
}

export type LexiconGenerationItem =
  | {
      kind: 'candidates'
      candidates: { headword: string; pos: Pos; transcription: string }[]
    }
  | { kind: 'structure'; entry: CachedEntry }
  | { kind: 'image'; prompt: string; src: string }
  | { kind: 'audio'; text: string; src: string }
  | { kind: 'indexed'; entryId: string }

export const PINNED_CELLS = {
  en: {
    NOUN: ['singular', 'plural'],
    ADJ: ['comparative', 'superlative'],
    VERB: [
      '3rd person singular',
      'past',
      'past participle',
      'present participle',
    ],
  },
  de: {
    NOUN: [
      'nominative singular',
      'genitive singular',
      'dative singular',
      'accusative singular',
      'nominative plural',
      'genitive plural',
      'dative plural',
      'accusative plural',
    ],
  },
  ru: {
    NOUN: [
      'nominative singular',
      'genitive singular',
      'dative singular',
      'accusative singular',
      'instrumental singular',
      'prepositional singular',
      'nominative plural',
      'genitive plural',
      'dative plural',
      'accusative plural',
      'instrumental plural',
      'prepositional plural',
    ],
  },
} as const satisfies Record<Language, Partial<Record<Pos, readonly string[]>>>

export function getPinnedCells(
  language: Language,
  pos: Pos,
): readonly string[] | undefined {
  const byLanguage: Partial<Record<Pos, readonly string[]>> =
    PINNED_CELLS[language]
  return byLanguage[pos]
}

const POS_VALUES = [
  'NOUN',
  'VERB',
  'ADJ',
  'ADV',
  'PRON',
  'DET',
  'NUM',
  'ADP',
  'CCONJ',
  'SCONJ',
  'PART',
  'INTJ',
] as const
const CEFR_VALUES = ['A1', 'A2', 'B1', 'B2', 'C1', 'C2'] as const

export const languageSchema = s.enum(LANGUAGES)

const posSchema = s.enum(POS_VALUES)
const cefrSchema = s.enum(CEFR_VALUES)
const spanSchema = s.transform(
  s.refine(s.array(s.number()), (value) => value.length === 2),
  (value): Span => [value[0], value[1]],
)
const exampleSchema = s.object({
  text: s.string(),
  node: spanSchema,
  translations: s.record(s.string(), s.string()),
  nodeAudioSrc: s.string().optional(),
  imagePrompt: s.string().optional(),
  imageSrc: s.string().optional(),
})
const cachedSenseSchema = s.object({
  id: s.string(),
  gloss: s.string(),
  translations: s.record(s.string(), s.string()),
  cefr: cefrSchema,
  labels: s.array(s.string()),
  example: exampleSchema,
})
const cachedWordFormSchema = s.object({
  cell: s.string(),
  form: s.string(),
  transcription: s.string(),
  example: exampleSchema,
})
const cachedEntryCommon = {
  id: s.string(),
  language: languageSchema,
  headword: s.string(),
  transcription: s.string(),
  audioSrc: s.string().optional(),
  translations: s.record(s.string(), s.string()),
  senses: s.record(s.string(), cachedSenseSchema),
}

export const cachedEntrySchema = s.discriminatedUnion('kind', [
  s.object({
    ...cachedEntryCommon,
    kind: s.literal('lexeme'),
    pos: posSchema,
    inflectionClass: s.string(),
    paradigm: s.record(s.string(), cachedWordFormSchema),
  }),
  s.object({ ...cachedEntryCommon, kind: s.literal('mwe') }),
])

const senseSchema = s.object({
  id: s.string(),
  gloss: s.string(),
  translations: s.record(s.string(), s.string()),
  cefr: cefrSchema,
  labels: s.array(s.string()),
  example: exampleSchema,
  senseRecallQuestionId: s.string().optional(),
  formRecallQuestionId: s.string().optional(),
})
const wordFormSchema = s.object({
  cell: s.string(),
  form: s.string(),
  transcription: s.string(),
  example: exampleSchema,
  formRecallQuestionId: s.string().optional(),
})
const lexiconEntryCommon = {
  id: s.string(),
  language: languageSchema,
  headword: s.string(),
  transcription: s.string(),
  audioSrc: s.string().optional(),
  translations: s.record(s.string(), s.string()),
  senses: s.record(s.string(), senseSchema),
}

export const lexiconEntrySchema = s.discriminatedUnion('kind', [
  s.object({
    ...lexiconEntryCommon,
    kind: s.literal('lexeme'),
    pos: posSchema,
    inflectionClass: s.string(),
    paradigm: s.record(s.string(), wordFormSchema),
    readAloudQuestionId: s.string(),
    dictationQuestionId: s.string(),
  }),
  s.object({ ...lexiconEntryCommon, kind: s.literal('mwe') }),
])

export const lexiconHitSchema = s.object({
  entryId: s.string(),
  kind: s.enum(['lexeme', 'mwe'] as const),
  headword: s.string(),
  pos: posSchema.optional(),
  transcription: s.string(),
  matchedField: s.enum(['headword', 'forms', 'translations']).optional(),
  matchedTranslation: s.string().optional(),
  matchedForm: s.string().optional(),
  translations: s.record(s.string(), s.string()),
  origin: s.enum(['pages', 'cache'] as const),
  pageId: s.string().optional(),
})

/**
 * Deterministic iteration order for senses: ascending sense id.
 *
 * Sense ids are minted in authoring order (ULID), so ascending id reproduces
 * the order a curator wrote them in, and two devices that added different
 * senses converge on the same reading order without a shared counter.
 */
export function entrySenses(entry: LexiconEntry): Sense[]
export function entrySenses(entry: CachedEntry): CachedSense[]
export function entrySenses(entry: CachedEntry | LexiconEntry): Sense[]
export function entrySenses(entry: {
  senses: Readonly<Record<string, { id: string }>>
}): { id: string }[] {
  return Object.values(entry.senses).toSorted((left, right) =>
    left.id < right.id ? -1 : 1,
  )
}

/**
 * Deterministic iteration order for a paradigm: the language's canonical cell
 * order (`PINNED_CELLS`) first, then any remaining cell alphabetically so an
 * unpinned cell can never make the order depend on insertion history.
 */
export function entryParadigm(entry: Lexeme): WordForm[]
export function entryParadigm(entry: CachedLexeme): CachedWordForm[]
export function entryParadigm(entry: CachedLexeme | Lexeme): WordForm[]
export function entryParadigm(entry: {
  language: Language
  pos: Pos
  paradigm: Readonly<Record<string, { cell: string }>>
}): { cell: string }[] {
  const forms = Object.values(entry.paradigm)
  const pinned = getPinnedCells(entry.language, entry.pos) ?? []
  const rank = (cell: string) => {
    const index = pinned.indexOf(cell)
    return index === -1 ? pinned.length : index
  }
  return forms.toSorted((left, right) => {
    const delta = rank(left.cell) - rank(right.cell)
    if (delta !== 0) return delta
    return left.cell < right.cell ? -1 : left.cell > right.cell ? 1 : 0
  })
}

/**
 * Drop CRDT tombstones before validating folded entry content.
 *
 * `crdt.merge` implements RFC 7396 deletion by writing `null` at the key, so a
 * removed sense or cell survives the fold as `{ [id]: null }`. The tombstone is
 * what makes the removal converge; it is not part of the entry, and the entry
 * schema must never see it.
 */
export function stripEntryTombstones(content: unknown): unknown {
  if (!_isRecord(content) || content.kind !== 'lexiconEntry') return content
  const entry = content.entry
  if (!_isRecord(entry)) return content
  // Recursive, and it must be: `replaceEntryPatch` tombstones at every depth,
  // so a removed `imageSrc` arrives as `imageSrc: null`. Stripping only the top
  // level of senses/paradigm leaves that null in the folded entry, where schema
  // validation rejects it and the fold silently keeps the last good content —
  // the removal appears to have done nothing.
  //
  // No entry field is legitimately null: absence is `undefined` throughout, so
  // every null here is a spent tombstone.
  return { ...content, entry: _stripNulls(entry) }
}

function _stripNulls(value: unknown): unknown {
  if (Array.isArray(value)) return value
  if (!_isRecord(value)) return value
  return Object.fromEntries(
    Object.entries(value)
      .filter(([, nested]) => nested !== null)
      .map(([key, nested]) => [key, _stripNulls(nested)]),
  )
}

function _isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value)
}

/** Keyed senses from an ordered list. */
export function sensesById<T extends { id: string }>(
  senses: readonly T[],
): Record<string, T> {
  return Object.fromEntries(senses.map((sense) => [sense.id, sense]))
}

/** Keyed paradigm from an ordered list. The cell is the key. */
export function paradigmByCell<T extends { cell: string }>(
  forms: readonly T[],
): Record<string, T> {
  return Object.fromEntries(forms.map((form) => [form.cell, form]))
}

/**
 * A whole-entry replace, expressed as a CRDT patch.
 *
 * Regeneration and re-pull replace an entry outright, but the page still merges
 * (policy: `crdtMerge`), so removals have to be stated: RFC 7396 `null` deletes
 * a key. Keys present in `next` are overwritten; keys only in `previous` are
 * tombstoned. Without this a re-pull could only ever add.
 */
export function replaceEntryPatch(
  previous: LexiconEntry,
  next: LexiconEntry,
): { kind: 'lexiconEntry'; entry: LexiconEntry } {
  return {
    kind: 'lexiconEntry',
    entry: _tombstoneRemoved(previous, next) as LexiconEntry,
  }
}

/**
 * `next`, with every key that `previous` had and `next` does not set to `null`.
 *
 * Recursive, because removal is not only about senses and cells: a regenerated
 * sense that drops its `imageSrc` merges over the old sense, and RFC 7396
 * leaves an absent key alone — so the stale image would survive a re-pull that
 * deliberately removed it. The same applies to `audioSrc`, `nodeAudioSrc` and
 * `imagePrompt`, and to `paradigm`/`pos` when a lexeme becomes an MWE.
 *
 * Arrays are replaced wholesale rather than diffed: a `Span` is a value, and
 * index-wise tombstoning would corrupt it.
 */
function _tombstoneRemoved(previous: unknown, next: unknown): unknown {
  if (!_isPlainObject(previous) || !_isPlainObject(next)) return next

  const merged: Record<string, unknown> = {}
  for (const [key, value] of Object.entries(next)) {
    merged[key] =
      key in previous ? _tombstoneRemoved(previous[key], value) : value
  }
  for (const key of Object.keys(previous)) {
    if (!(key in next)) merged[key] = null
  }
  return merged
}

function _isPlainObject(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value)
}

/**
 * The learner's translation, or the best available one.
 *
 * An entry has no translation into its OWN language, so asking for the English
 * translation of an English word yields nothing — and an empty string is a card
 * with no prompt on it. Falling back to any translation the entry does have
 * keeps the card answerable; the caller choosing a sensible L1 keeps it useful.
 */
export function translationFor(
  translations: Record<TranslationLanguage, string>,
  language?: TranslationLanguage,
): string {
  const exact = language === undefined ? undefined : translations[language]
  if (exact) return exact
  return Object.values(translations).find(Boolean) ?? ''
}

const NATURAL_KEY_SEPARATOR = '\u001F'
const ULID_PATTERN = /^[0-9A-Z_a-z]{19}$/
const LETTER_PATTERN = /\p{L}/u

export function entryNaturalKey(entry: CachedEntry | LexiconEntry): string {
  const parts = [entry.language, entry.headword]
  if (entry.kind === 'lexeme') parts.push(entry.pos)
  parts.push(entry.transcription)
  if (entry.kind === 'lexeme') parts.push(entry.inflectionClass)
  return parts.join(NATURAL_KEY_SEPARATOR)
}

export function validateEntry(entry: CachedEntry | LexiconEntry): string[] {
  const violations: string[] = []

  for (const [index, sense] of entrySenses(entry).entries()) {
    _validateExample(sense.example, `Sense ${index} example`, violations)
  }

  if (entry.kind === 'lexeme') {
    const cells = new Set<string>()
    for (const [index, wordForm] of entryParadigm(entry).entries()) {
      if (cells.has(wordForm.cell)) {
        violations.push(`Paradigm has duplicate cell "${wordForm.cell}"`)
      }
      cells.add(wordForm.cell)
      _validateExample(
        wordForm.example,
        `Word form ${index} example`,
        violations,
      )
      if (
        !wordForm.example.text
          .toLocaleLowerCase(entry.language)
          .includes(wordForm.form.toLocaleLowerCase(entry.language))
      ) {
        violations.push(
          `Word form ${index} example does not contain form "${wordForm.form}"`,
        )
      }
    }
  }

  _validateQuestionIds(entry, violations)
  return violations
}

export function assertValidEntry(entry: CachedEntry | LexiconEntry): void {
  const violations = validateEntry(entry)
  if (violations.length > 0) {
    throw new Error(`Invalid lexicon entry:\n- ${violations.join('\n- ')}`)
  }
}

export function adoptEntryContent(
  cached: CachedEntry,
  opts: {
    selectedSenseIds?: string[]
    mintId: () => string
  },
): LexiconEntry {
  const cachedSenses = entrySenses(cached)
  const selectedSenseIds = new Set(
    opts.selectedSenseIds ?? cachedSenses.map((sense) => sense.id),
  )
  const adoptSenses = () =>
    sensesById(
      cachedSenses.map((sense) =>
        _adoptSense(sense, selectedSenseIds.has(sense.id), opts.mintId),
      ),
    )

  if (cached.kind === 'mwe') {
    return {
      ...cached,
      kind: 'mwe',
      translations: { ...cached.translations },
      senses: adoptSenses(),
    }
  }

  const readAloudQuestionId = opts.mintId()
  const dictationQuestionId = opts.mintId()
  const senses = adoptSenses()
  const paradigm = paradigmByCell(
    entryParadigm(cached).map((form) => _adoptWordForm(form, opts.mintId)),
  )
  return {
    ...cached,
    kind: 'lexeme',
    translations: { ...cached.translations },
    senses,
    paradigm,
    readAloudQuestionId,
    dictationQuestionId,
  }
}

function _adoptSense(
  sense: CachedSense,
  selected: boolean,
  mintId: () => string,
): Sense {
  return {
    ...sense,
    translations: { ...sense.translations },
    labels: [...sense.labels],
    example: _adoptExample(sense.example),
    ...(selected
      ? {
          senseRecallQuestionId: mintId(),
          formRecallQuestionId: mintId(),
        }
      : {}),
  }
}

function _adoptWordForm(
  wordForm: CachedWordForm,
  mintId: () => string,
): WordForm {
  return {
    ...wordForm,
    example: _adoptExample(wordForm.example),
    formRecallQuestionId: mintId(),
  }
}

function _adoptExample(example: Example): Example {
  return { ...example, translations: { ...example.translations } }
}

function _validateExample(
  example: Example,
  address: string,
  violations: string[],
) {
  const [offset, length] = example.node
  const end = offset + length
  if (offset < 0 || length <= 0 || end > example.text.length) {
    violations.push(
      `${address} node [${offset}, ${length}] lies outside text length ${example.text.length}`,
    )
    return
  }
  if (
    (offset > 0 && LETTER_PATTERN.test(example.text[offset - 1])) ||
    (end < example.text.length && LETTER_PATTERN.test(example.text[end]))
  ) {
    violations.push(
      `${address} node [${offset}, ${length}] is not word-bounded`,
    )
  }
}

function _validateQuestionIds(
  entry: CachedEntry | LexiconEntry,
  violations: string[],
) {
  const ids: string[] = []
  for (const sense of entrySenses(entry)) {
    const { senseRecallQuestionId, formRecallQuestionId } = sense
    if (
      (senseRecallQuestionId === undefined) !==
      (formRecallQuestionId === undefined)
    ) {
      violations.push(`Sense "${sense.id}" has only one question id`)
    }
    if (senseRecallQuestionId) ids.push(senseRecallQuestionId)
    if (formRecallQuestionId) ids.push(formRecallQuestionId)
  }
  if (entry.kind === 'lexeme') {
    if ('readAloudQuestionId' in entry) {
      ids.unshift(entry.readAloudQuestionId, entry.dictationQuestionId)
    }
    for (const form of entryParadigm(entry)) {
      if (form.formRecallQuestionId) ids.push(form.formRecallQuestionId)
    }
  }

  const seen = new Set<string>()
  for (const id of ids) {
    if (!ULID_PATTERN.test(id))
      violations.push(`Question id "${id}" is not a valid ULID`)
    if (seen.has(id)) violations.push(`Question id "${id}" is duplicated`)
    seen.add(id)
  }
}

/**
 * Every bucket path an entry's generated media occupies.
 *
 * Absolute URLs are excluded: a bucket addresses its objects by path, so a
 * `https://…` src is served from somewhere neither bucket can delete.
 */
export function entryMediaPaths(entry: CachedEntry | LexiconEntry): string[] {
  return [
    entry.audioSrc,
    ...entrySenses(entry).flatMap((sense) => [
      sense.example.imageSrc,
      sense.example.nodeAudioSrc,
    ]),
    ...(entry.kind === 'lexeme'
      ? entryParadigm(entry).flatMap((form) => [
          form.example.imageSrc,
          form.example.nodeAudioSrc,
        ])
      : []),
  ].filter(
    (path): path is string =>
      path !== undefined &&
      path !== '' &&
      !/^(?:[a-z][a-z0-9+.-]*:|\/\/)/iu.test(path),
  )
}
